import { useEffect, type RefObject } from 'react';
import { useCameraStore } from '@/store/cameraStore';
import { useSettingsStore } from '@/store/settingsStore';
import { drawGrid, type GridPalette } from '@/core/rendering/gridRenderer';
import { useEngine } from '@/app/EngineContext';

const readPalette = (el: HTMLElement): GridPalette => {
  const css = getComputedStyle(el);
  return {
    minor: css.getPropertyValue('--grid-minor').trim() || 'rgba(60,52,40,0.10)',
    major: css.getPropertyValue('--grid-major').trim() || 'rgba(60,52,40,0.20)',
    axis: css.getPropertyValue('--grid-axis').trim() || 'rgba(60,52,40,0.35)',
    origin: css.getPropertyValue('--focus').trim() || '#b5522a',
  };
};

/**
 * Draws the adaptive grid onto a canvas element, once per frame at most, whenever the
 * camera or viewport changes. The backing store is sized for the device pixel ratio
 * so lines stay crisp; the draw itself is a few hundred line segments at most.
 */
export const useGridRenderer = (canvasRef: RefObject<HTMLCanvasElement | null>): void => {
  const { frames } = useEngine();
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    let palette = readPalette(canvas);
    let sizedFor = { width: -1, height: -1, dpr: -1 };

    const draw = () => {
      const { camera, viewport } = useCameraStore.getState();
      const { width, height } = viewport.size;
      const dpr = viewport.devicePixelRatio;
      if (width === 0 || height === 0) return;
      if (sizedFor.width !== width || sizedFor.height !== height || sizedFor.dpr !== dpr) {
        canvas.width = Math.round(width * dpr);
        canvas.height = Math.round(height * dpr);
        sizedFor = { width, height, dpr };
        palette = readPalette(canvas);
      }
      if (useSettingsStore.getState().gridVisible) drawGrid(ctx, camera, viewport.size, dpr, palette);
      else {
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, width, height);
      }
    };

    draw();
    const unsubscribe = useCameraStore.subscribe((s, prev) => {
      if (s.camera !== prev.camera || s.viewport !== prev.viewport) frames.schedule(draw);
    });
    const unsubSettings = useSettingsStore.subscribe((s, prev) => {
      if (s.gridVisible !== prev.gridVisible) frames.schedule(draw);
    });
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onScheme = () => {
      palette = readPalette(canvas);
      frames.schedule(draw);
    };
    media.addEventListener('change', onScheme);
    return () => {
      unsubscribe();
      unsubSettings();
      media.removeEventListener('change', onScheme);
    };
  }, [frames, canvasRef]);
};
